import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Printer, X, ArrowRight } from "lucide-react";

const formatDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
const fmtQty = (q) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(q || 0);

const TransferDocumentPage = () => {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const { settings, logoUrl } = useSettings();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/transfers/${transferId}`).then((r) => setT(r.data)).catch(() => toast.error("Transfert introuvable")).finally(() => setLoading(false));
  }, [transferId]);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="spinner"></div></div>;
  if (!t) return <div className="flex flex-col items-center justify-center h-screen gap-3"><p className="text-slate-500">Document indisponible</p><Button variant="outline" onClick={() => navigate("/transfers")}>Fermer</Button></div>;

  return (
    <div className="min-h-screen bg-slate-100 p-4 lg:p-8" data-testid="transfer-document-page">
      <div className="max-w-4xl mx-auto mb-4 flex items-center justify-between no-print">
        <Button variant="outline" className="rounded-sm" onClick={() => navigate("/transfers")} data-testid="transfer-close-btn"><X className="w-4 h-4 mr-2" /> Fermer</Button>
        <Button onClick={() => window.print()} className="btn-primary" data-testid="print-transfer-btn"><Printer className="w-4 h-4 mr-2" /> Imprimer</Button>
      </div>

      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-sm p-8 purchase-sheet">
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt={settings.name} className="w-14 h-14 rounded-sm object-contain" />
            <div>
              <h1 className="text-lg font-bold font-[Chivo] text-slate-900">{settings.name}</h1>
              {settings.slogan && <p className="text-xs text-slate-500">{settings.slogan}</p>}
              <p className="text-xs text-slate-500">{[settings.address, settings.phone && `Tél: ${settings.phone}`, settings.email].filter(Boolean).join(" — ")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold font-[Chivo] text-slate-900">BON DE TRANSFERT</p>
            <p className="font-mono text-sm" data-testid="transfer-number">{t.number}</p>
            <p className="text-xs text-slate-500">du {formatDate(t.created_at)}</p>
            {t.status === "cancelled" && <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">ANNULÉ</span>}
          </div>
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 mb-6 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3"><p className="overline mb-1">Dépôt source</p><p className="font-bold text-slate-900 text-base" data-testid="transfer-from">{t.from_depot_name}</p></div>
          <ArrowRight className="w-8 h-8 text-blue-700" />
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3"><p className="overline mb-1">Dépôt destination</p><p className="font-bold text-slate-900 text-base" data-testid="transfer-to">{t.to_depot_name}</p></div>
        </div>

        <table className="w-full text-sm mb-6" data-testid="transfer-items-table">
          <thead className="bg-slate-100 text-[11px] uppercase text-slate-600">
            <tr><th className="text-left px-3 py-2 w-10">#</th><th className="text-left px-3 py-2">Désignation</th><th className="text-left px-3 py-2 w-28">Unité</th><th className="text-right px-3 py-2 w-32">Quantité</th><th className="text-center px-3 py-2 w-28">Contrôle</th></tr>
          </thead>
          <tbody>
            {t.items.map((i, idx) => (
              <tr key={idx} className="border-b border-slate-200">
                <td className="px-3 py-1.5 text-slate-400 font-mono text-xs">{idx + 1}</td>
                <td className="px-3 py-1.5 font-medium text-slate-900">{i.product_name}</td>
                <td className="px-3 py-1.5 text-slate-600">{i.unit || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono font-bold">{fmtQty(i.quantity)}</td>
                <td className="px-3 py-1.5 text-center"><span className="inline-block w-5 h-5 border-2 border-slate-400 rounded-sm" /></td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="bg-slate-900 text-white"><td colSpan={3} className="px-3 py-2 font-bold text-right">TOTAL QUANTITÉS · {t.items.length} article{t.items.length > 1 ? "s" : ""}</td><td className="px-3 py-2 text-right font-mono font-bold" data-testid="transfer-total-qty">{fmtQty(t.total_quantity)}</td><td></td></tr></tfoot>
        </table>

        {t.notes && <div className="border border-slate-200 rounded-sm p-3 text-sm mb-6"><p className="overline mb-1">Observations</p><p>{t.notes}</p></div>}
        <p className="text-xs text-slate-500 mb-8">Établi par : <span className="font-medium text-slate-900">{t.created_by}</span>{t.edited_by && <> · modifié par {t.edited_by}</>}</p>

        <div className="grid grid-cols-3 gap-8 text-sm">
          <div><p className="font-medium mb-12">Remis par (dépôt source)</p><div className="border-t border-slate-400" /></div>
          <div><p className="font-medium mb-12">Transporté par</p><div className="border-t border-slate-400" /></div>
          <div><p className="font-medium mb-12">Reçu par (dépôt destination)</p><div className="border-t border-slate-400" /></div>
        </div>
      </div>
    </div>
  );
};

export default TransferDocumentPage;
